import { useEffect, useMemo, useRef } from "react";
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { BODY_CSS, HEADING_CSS, blockStyle, splitBlocks, renderInline } from "@/components/PDFPreviews";

// ---------------------------------------------------------------------------------------------
// A "Word-like" editor: type and see bold/headings/alignment directly, no visible <h1>/<b>/
// align="..." tags — the tags still exist, they're just what gets *saved*, never what's shown
// while typing. That requires a real editable surface (`contentEditable`), not a toolbar over a
// plain `<textarea>`.
//
// **The risk that ruled contentEditable out the first time — "can accumulate hidden markup from
// pasted content" — is closed two ways, not assumed away:**
//   1. Paste is intercepted and forced to plain text (see handlePaste), so nothing pasted ever
//      carries its own formatting/markup in.
//   2. Every edit is re-serialized through `serializeEditor` below, which walks the *actual* DOM
//      the browser produced and keeps only the exact allow-list `core.pdf`/`renderInline` already
//      render (h1-h6, p align, b/i/u, font size) — anything else the browser inserted (a bare
//      <div>, a <span style="...">, whatever Firefox vs. Chrome happen to disagree about) is
//      unwrapped to its own inline content, never passed through. What gets saved is always in
//      the same shape a hand-typed `<h1>title</h1>` would be, never raw innerHTML.
// ---------------------------------------------------------------------------------------------

const BLOCK_LEVEL = { H1: 1, H2: 2, H3: 3, H4: 4, H5: 5, H6: 6 };

const escapeText = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function serializeInline(node) {
  let out = "";
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) { out += escapeText(child.nodeValue); continue; }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const tag = child.tagName;
    if (tag === "BR") { out += "\n"; continue; }
    if (tag === "B" || tag === "STRONG") { out += `<b>${serializeInline(child)}</b>`; continue; }
    if (tag === "I" || tag === "EM") { out += `<i>${serializeInline(child)}</i>`; continue; }
    if (tag === "U") { out += `<u>${serializeInline(child)}</u>`; continue; }
    if (tag === "FONT" && child.getAttribute("size")) {
      out += `<font size="${child.getAttribute("size")}">${serializeInline(child)}</font>`;
      continue;
    }
    // Anything else the browser produced (a bare <span>, a <div> from a pasted fragment that
    // slipped past onPaste some other way) is unwrapped to its own inline content — the wrapper
    // itself is what's outside the allow-list, so it never survives, but the text inside still
    // does (better than losing it outright).
    out += serializeInline(child);
  }
  return out;
}

const blockAlign = (el) => {
  const a = (el.style.textAlign || "").toLowerCase();
  return a === "left" || a === "center" || a === "right" ? a : undefined;
};

/** The contentEditable's live DOM -> the same `body_markdown` string core.pdf/PDFPreviews already
 *  know how to render. Exported so it can be unit-tested independently of the DOM event wiring. */
export function serializeEditor(root) {
  const blocks = [];
  for (const el of root.children) {
    const level = BLOCK_LEVEL[el.tagName];
    const align = blockAlign(el);
    const raw = serializeInline(el);
    if (!raw.trim()) continue; // an Enter with nothing typed contributes nothing
    if (level) blocks.push(align ? `<h${level} align="${align}">${raw}</h${level}>` : `<h${level}>${raw}</h${level}>`);
    else if (align) blocks.push(`<p align="${align}">${raw}</p>`);
    else blocks.push(raw);
  }
  return blocks.join("\n\n");
}

/** One block, rendered as real (editable) DOM — headings as real `<hN>`, everything else as a
 *  `<p>` (contentEditable's own Enter key produces `<p>` once `defaultParagraphSeparator` is set
 *  below, so new paragraphs the admin types match this shape without any translation). Carries
 *  its own inline style so it visually matches the printed/previewed version immediately, rather
 *  than the browser's own default heading styling. */
function Block({ kind, align, children }) {
  const m = /^h([1-6])$/.exec(kind || "");
  const Tag = m ? kind : "p";
  const style = { ...blockStyle(kind, align), margin: 0, marginBottom: (m ? HEADING_CSS[m[1]] : BODY_CSS).marginBottom };
  return <Tag style={style}>{children}</Tag>;
}

const STYLE_OPTIONS = [
  { value: "p", label: "Normal" },
  { value: "h1", label: "Heading 1 (title)" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "h4", label: "Heading 4" },
  { value: "h5", label: "Heading 5" },
  { value: "h6", label: "Heading 6" },
];
const SIZE_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

/** Re-apply this app's own typography to whatever block the selection is currently in, after a
 *  command that changes block type (`formatBlock`) — otherwise a new heading renders in the
 *  browser's own default heading style (larger, serif, its own spacing) until the page reloads,
 *  because `execCommand` only sets the tag, not our styling. */
function restyleCurrentBlock(root) {
  const sel = window.getSelection();
  if (!sel || !sel.anchorNode) return;
  let node = sel.anchorNode;
  while (node && node !== root && node.parentElement !== root) node = node.parentElement;
  if (!node || node === root || !(node instanceof HTMLElement)) return;
  const m = /^H([1-6])$/.exec(node.tagName);
  const kind = m ? `h${m[1]}` : "p";
  Object.assign(node.style, blockStyle(kind, blockAlign(node)));
}

/**
 * Word-like formatting toolbar + editable surface — the admin Template Catalog's `body_markdown`
 * editor and AdminDocumentEditor's per-case document body both use it, in place of a toolbar over
 * a plain `<textarea>` that only ever showed and saved the raw markup.
 *
 * **Uncontrolled after mount, on purpose.** React re-rendering a contentEditable's children on
 * every keystroke is the classic way to fight the browser for control of the cursor. The initial
 * content is rendered once (via `splitBlocks`/`renderInline` — the same safe, allow-list-only
 * parse used everywhere else, never raw `innerHTML` from an unvalidated string) and after that the
 * DOM belongs to the browser; edits flow *out* via `onChange(serializeEditor(...))` but nothing
 * flows back in except through `resetKey` changing (switching templates, or Import JSON loading a
 * new body) — remounting the whole surface is simpler and safer than trying to diff live editable
 * DOM against a new value.
 */
//
// `toolbarExtra` renders at the end of the toolbar, after Size — for controls that belong to the
// page hosting the editor rather than to the text (the Template Catalog's pane-layout toggle).
export default function RichTextEditor({ value, onChange, resetKey, contentClassName = "", contentStyle, testId = "rich-text-editor", toolbarExtra }) {
  const rootRef = useRef(null);
  const initialBlocks = useMemo(() => splitBlocks(value), [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Chrome/Firefox/Edge all still honour this: Enter creates <p>, matching what a freshly
    // typed "Normal" paragraph should serialize as. Without it, contentEditable's own default
    // (often a bare <div>) still round-trips through serializeEditor fine (an unrecognised tag
    // just falls through to a plain "p" block) — this only makes the live DOM read cleanly too.
    try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch { /* best-effort */ }
  }, []);

  const handleInput = () => onChange(serializeEditor(rootRef.current));

  const handlePaste = (e) => {
    // The one paste-borne risk contentEditable actually has: formatting (or worse) riding in on
    // the clipboard. Forcing plain text closes it outright — nothing pasted ever carries markup.
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const exec = (cmd, arg) => {
    rootRef.current?.focus();
    document.execCommand(cmd, false, arg);
    restyleCurrentBlock(rootRef.current);
    handleInput();
  };
  // onMouseDown + preventDefault, not onClick: a click on the button would otherwise blur the
  // editor and collapse the selection *before* the command runs, which is the standard failure
  // mode for any contentEditable toolbar — by the time onClick fired there would be nothing left
  // to apply the command to.
  const mouseDownExec = (cmd, arg) => (e) => { e.preventDefault(); exec(cmd, arg); };

  const Btn = ({ testid, onMouseDown, icon: Icon, label }) => (
    <button type="button" data-testid={testid} title={label} onMouseDown={onMouseDown}
            className="p-1.5 rounded text-[#A0AABF] hover:text-white hover:bg-white/10 transition-colors">
      <Icon className="w-3.5 h-3.5"/>
    </button>
  );
  const selectCls = "h-7 rounded border border-white/10 bg-[#080C16] text-white text-xs px-1.5";
  const Sep = () => <div className="w-px h-4 bg-white/10"/>;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap border border-white/10 rounded-md p-1 bg-black/20 w-fit">
        <select data-testid="markup-style" value="" title="Style" className={selectCls}
                onChange={(e) => { const v = e.target.value; if (v) exec("formatBlock", `<${v}>`); e.target.value = ""; }}>
          <option value="" disabled hidden>Style</option>
          {STYLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Sep/>
        <Btn testid="markup-bold" icon={Bold} label="Bold" onMouseDown={mouseDownExec("bold")}/>
        <Btn testid="markup-italic" icon={Italic} label="Italic" onMouseDown={mouseDownExec("italic")}/>
        <Btn testid="markup-underline" icon={Underline} label="Underline" onMouseDown={mouseDownExec("underline")}/>
        <Sep/>
        <Btn testid="markup-align-left" icon={AlignLeft} label="Align left" onMouseDown={mouseDownExec("justifyLeft")}/>
        <Btn testid="markup-align-center" icon={AlignCenter} label="Align centre" onMouseDown={mouseDownExec("justifyCenter")}/>
        <Btn testid="markup-align-right" icon={AlignRight} label="Align right" onMouseDown={mouseDownExec("justifyRight")}/>
        <Sep/>
        <label className="text-[10px] text-[#6B7280] uppercase tracking-wide">Size</label>
        <select data-testid="markup-size" value="" title="Text size" className={selectCls}
                onChange={(e) => { const n = e.target.value; if (n) exec("fontSize", n); e.target.value = ""; }}>
          <option value="" disabled hidden>Size</option>
          {SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}{n === 3 ? " (normal)" : ""}</option>)}
        </select>
        {toolbarExtra && <><Sep/>{toolbarExtra}</>}
      </div>
      <div
        ref={rootRef}
        key={resetKey}
        data-testid={testId}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        className={`flex-1 min-h-0 overflow-y-auto rounded border border-white/10 bg-[#FEFDF8] px-4 py-3 outline-none focus:ring-1 focus:ring-[#CBA96C] ${contentClassName}`}
        style={{ color: "#112240", ...contentStyle }}
      >
        {initialBlocks.map((b, i) => (
          <Block key={i} kind={b.kind} align={b.align}>{renderInline(b.text)}</Block>
        ))}
      </div>
    </div>
  );
}
